import dayjs, { Dayjs } from "dayjs";
import { ApiLocalTime, CollectionReceptionSlot, CollectionSlot } from "@/types";

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * Normalises a collections-API time of day to "HH:mm:ss".
 *
 * The endpoint is documented as returning a `LocalTime` object, but the reception endpoints return
 * the same DTO's times as strings. Accepting both means a change of serialisation on the backend
 * can't break the slot pickers.
 */
export const toTimeString = (time: ApiLocalTime): string =>
  typeof time === "string" ? time : `${pad(time.hour)}:${pad(time.minute)}:${pad(time.second ?? 0)}`;

/** Time of day for display, e.g. "08:00" — the repo's `.slice(0, 5)` convention, post-normalise. */
export const formatSlotTime = (time: ApiLocalTime): string => toTimeString(time).slice(0, 5);

/** "08:00 – 09:00" — the slot's full arrival window. */
export const formatSlotRange = (slot: CollectionReceptionSlot): string =>
  `${formatSlotTime(slot.startTime)} – ${formatSlotTime(slot.endTime)}`;

/** The slot's start as a local ISO datetime, e.g. "2026-09-02T08:00:00". */
export const slotDateTimeISO = (slot: CollectionReceptionSlot): string =>
  `${slot.slotDate}T${toTimeString(slot.startTime)}`;

/**
 * A slot can be booked only when the site has capacity left *and* at least one driver is free for
 * it. Unbookable slots are still returned, so both halves have to be checked.
 */
export const isSlotBookable = (entry: CollectionSlot): boolean =>
  entry.availableDrivers.length > 0 && entry.slot.remainingCapacity > 0;

/**
 * Past once the arrival window has *closed* — a 09:00–10:00 slot is still a legitimate arrival at
 * 09:30. Matches `isSlotPast` in `@/utils/ReceptionSlotGrid`, which does the same for deliveries.
 */
export const isSlotPast = (slot: CollectionReceptionSlot, now: Dayjs = dayjs()): boolean =>
  dayjs(`${slot.slotDate}T${toTimeString(slot.endTime)}`).isBefore(now);

/**
 * Whether a slot can be picked right now. Past and full always block it. A slot with no free driver
 * blocks it too when scheduling a new collection, but not when moving an existing one — see
 * `requireAvailableDriver` on `CollectionSlotSection` for why.
 */
export const isSlotSelectable = (
  entry: CollectionSlot,
  options: { requireAvailableDriver?: boolean; now?: Dayjs } = {}
): boolean => {
  const { requireAvailableDriver = true, now } = options;
  if (isSlotPast(entry.slot, now)) return false;
  if (entry.slot.remainingCapacity <= 0) return false;
  return !requireAvailableDriver || entry.availableDrivers.length > 0;
};

/** Chronological order by start time. Does not mutate the input. */
export const sortSlotEntries = (entries: CollectionSlot[]): CollectionSlot[] =>
  [...entries].sort((a, b) => toTimeString(a.slot.startTime).localeCompare(toTimeString(b.slot.startTime)));
